/**
 * Model routing (God Mode): every decision about where an LLM call goes, on
 * one page. Loads GET /api/admin/tier-models and the workspace list once and
 * hands `refresh` to every card; each card owns its own mutations.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import type { TierModelsResponse, WorkspaceLite } from "./types.js";
import { describeError, responseError } from "./http.js";
import { ErrorAlert } from "./styles.js";
import { RoutingPolicyCard } from "./RoutingPolicyCard.js";
import { LanesCard } from "./LanesCard.js";
import { TierModelsCard } from "./TierModelsCard.js";
import { WorkspacesCard } from "./WorkspacesCard.js";
import { ModelHealthCard } from "./ModelHealthCard.js";
import { ChangelogCard } from "./ChangelogCard.js";
import { EvalJudgesCard } from "./EvalJudgesCard.js";

export function ModelRoutingPage() {
  const [data, setData] = useState<TierModelsResponse | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Once the page has data, a failed refresh must not blank it: the cards
  // (the Lanes outage switches above all) stay mounted and the failure
  // lands in the page alert instead.
  const hasData = useRef(false);

  const refresh = useCallback(async () => {
    const fail = (message: string) => {
      if (hasData.current) setActionError(message);
      else setLoadError(message);
    };
    try {
      const [res, workspacesRes] = await Promise.all([
        fetch("/api/admin/tier-models"),
        fetch("/api/admin/workspaces"),
      ]);
      if (!res.ok) {
        fail(await responseError(res));
        return;
      }
      setData((await res.json()) as TierModelsResponse);
      hasData.current = true;
      setLoadError(null);
      if (workspacesRes.ok) {
        setWorkspaces((await workspacesRes.json()) as WorkspaceLite[]);
      } else {
        setActionError(`Unable to load the workspace list: ${await responseError(workspacesRes)}`);
      }
    } catch (err) {
      // Every card's refetch funnels through here; keep the raw error for the console.
      console.error("[model-routing] refresh failed", err);
      fail(describeError(err, "Unable to load model routing"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin" style={{ color: "var(--text-tertiary)" }} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-sm" role="alert" style={{ color: "var(--error)" }}>
        {loadError ?? "Unable to load model routing."}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {actionError && <ErrorAlert message={actionError} />}
      <RoutingPolicyCard
        policy={data.policy}
        lanes={data.lanes}
        workspaces={data.workspaces}
        totalWorkspaces={workspaces.length}
      />
      <LanesCard lanes={data.lanes} onChanged={refresh} onError={setActionError} />
      <TierModelsCard data={data} onChanged={refresh} onError={setActionError} />
      <WorkspacesCard
        workspaces={data.workspaces}
        allWorkspaces={workspaces}
        catalog={data.catalog}
        lanes={data.lanes}
        policy={data.policy}
        tiers={data.tiers}
        onChanged={refresh}
        onError={setActionError}
      />
      <ModelHealthCard />
      <ChangelogCard changelog={data.changelog} />
      <EvalJudgesCard workspaces={workspaces} />
    </div>
  );
}
